import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import PageHeader from '@/components/feature/PageHeader';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { timelineFeed } from '@/mocks/timeline';
import { useSeo } from '@/hooks/useSeo';

const TAG_STYLE: Record<string, string> = {
  反转: 'bg-accent-500 text-accent-950',
  回应: 'bg-secondary-500 text-background-50',
  发酵: 'bg-primary-500 text-background-50',
  存疑: 'bg-accent-100 text-accent-900',
  归档: 'bg-background-300 text-foreground-700',
  源头: 'bg-primary-500 text-background-50',
};

const FILTERS = ['全部', '娱乐圈', '网红圈', '商界', '电竞圈', '体育圈', '行业观察'];

export default function TimelinePage() {
  const [filter, setFilter] = useState('全部');

  useSeo({
    title: '网曝黑料事件时间线 - 全程梳理与进度更新 | 瓜田档案局',
    description:
      '按时间顺序梳理网曝黑料事件的每一个关键节点，从爆料源头、首次回应到反转进展，持续更新。帮你快速看清事情到底是怎么一步步走到今天的。',
    keywords: '黑料时间线,事件时间线,网曝黑料,爆料进度,反转整理',
    path: '/timeline',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '网曝黑料事件时间线',
      description: '按时间顺序整理的网曝黑料事件关键节点。',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hychesuan.cn/' },
          { '@type': 'ListItem', position: 2, name: '事件时间线', item: 'https://hychesuan.cn/timeline' },
        ],
      },
    },
  });

  const groups = useMemo(() => {
    const list = timelineFeed.filter((item) => filter === '全部' || item.categoryName === filter);
    const map = new Map<string, typeof timelineFeed>();
    list.forEach((item) => {
      const arr = map.get(item.date) || [];
      arr.push(item);
      map.set(item.date, arr);
    });
    return Array.from(map.entries());
  }, [filter]);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <PageHeader
          eyebrow="事件时间线"
          title="一条线看完：从爆料源头到最新进展"
          desc="所有节点均按公开信息的发布时间排列。越靠上越新，标有「反转」的节点表示事件方向发生了明显变化。"
        />

        <div className="mx-auto max-w-content px-4 md:px-6 py-10 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '事件时间线' }]} />

          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  filter === item
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {groups.length > 0 ? (
            <div className="mt-10">
              {groups.map(([date, items]) => (
                <section key={date} className="mb-12 last:mb-0">
                  <div className="mb-5 flex items-center gap-4">
                    <span className="flex h-12 w-16 items-center justify-center rounded-md bg-foreground-950 font-heading text-sm font-bold text-background-50">
                      {date}
                    </span>
                    <span className="h-px flex-1 bg-background-200"></span>
                    <span className="text-xs text-foreground-500">{items.length} 条更新</span>
                  </div>

                  <ol className="relative border-l-2 border-background-200 pl-6 md:pl-8">
                    {items.map((item) => (
                      <li key={`${item.date}-${item.time}-${item.slug}`} className="relative pb-7 last:pb-0">
                        <span className="absolute -left-[31px] top-2 h-3.5 w-3.5 rounded-full border-2 border-background-50 bg-secondary-500 md:-left-[39px]"></span>
                        <div className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="font-heading text-sm font-bold text-foreground-950">
                              {item.time}
                            </span>
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                                TAG_STYLE[item.tag] || 'bg-background-200 text-foreground-700'
                              }`}
                            >
                              {item.tag}
                            </span>
                            <span className="text-xs text-foreground-500">{item.categoryName}</span>
                          </div>
                          <h2 className="mt-3 font-heading text-base font-bold text-foreground-950 md:text-lg">
                            <Link to={`/article/${item.slug}`} className="hover:text-primary-600 transition-colors">
                              {item.title}
                            </Link>
                          </h2>
                          <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                          <Link
                            to={`/article/${item.slug}`}
                            className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                          >
                            查看完整档案
                            <i className="ri-arrow-right-line"></i>
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <i className="ri-time-line text-3xl text-foreground-400"></i>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                该圈子暂无时间线更新
              </h2>
              <p className="mt-2 text-sm text-foreground-600">切换其他圈子，或回到「全部」查看完整进度。</p>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}